import styled from 'styled-components';
import DateFormatted from '@components/dateFormatted/DateFormatted';
import { getCountingMethodConfig } from '../results/countingMethodConfig';
import { getModelLabel } from '@config/videoDetectionModelConfig';

const InfoItem = styled.div`
	min-width: 0;
	margin-bottom: 4px;
`;

const Label = styled.label`
	display: block;
	font-size: 10px;
	text-transform: uppercase;
	letter-spacing: 0.5px;
	color: ${({ theme }) => theme.colors.textAccent};
	opacity: 0.8;
	margin-bottom: 1px;
`;

const Value = styled.div`
	font-size: 12px;
	color: rgba(255, 255, 255, 0.95);
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

const InfoRow = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 4px;
`;

const StatusBadge = styled.span`
	display: inline-flex;
	align-items: center;
	gap: 5px;
	padding: 2px 8px;
	border-radius: 4px;
	font-size: 10px;
	font-weight: 600;
	text-transform: capitalize;
	color: #fff;
	background-color: ${({ $status }) => {
		switch ($status) {
			case 'finished':
				return '#27ae60';
			case 'running':
				return '#3b82f6';
			case 'pending':
				return '#f39c12';
			case 'failed':
				return '#e74c3c';
			default:
				return '#6c757d';
		}
	}};
`;

const ProgressBar = styled.div`
	height: 4px;
	border-radius: 2px;
	background: rgba(255, 255, 255, 0.1);
	overflow: hidden;
`;

const ProgressFill = styled.div`
	height: 100%;
	border-radius: 2px;
	background: ${({ $status }) => ($status === 'failed' ? '#e74c3c' : '#27ae60')};
	width: ${({ $value }) => $value}%;
	transition: width 0.3s ease;
`;

const ErrorText = styled.div`
	font-size: 11px;
	color: #e74c3c;
	margin-top: 2px;
`;

const MessageText = styled(Value)`
	white-space: normal;
	line-height: 1.3;
`;

const JobSection = ({ job }) => {
	if (!job) return null;

	const status = job.job_status || 'unknown';
	const progress = job.progress ?? null;
	const isFailed = status === 'failed';
	const countingLabel = getCountingMethodConfig(job.counting_method).methodLabel;
	const modelLabel = getModelLabel(job.model_id);

	return (
		<>
			{/* Row 1: Status + Progress */}
			<InfoRow>
				<InfoItem>
					<Label>Status</Label>
					<StatusBadge $status={status}>{status}</StatusBadge>
				</InfoItem>
				{progress != null && (
					<InfoItem>
						<Label>Progress</Label>
						<Value>{progress}%</Value>
						<ProgressBar>
							<ProgressFill $value={progress} $status={status} />
						</ProgressBar>
					</InfoItem>
				)}
			</InfoRow>

			{/* Row 2: Model + Counting method */}
			<InfoRow>
				{job.model_id && (
					<InfoItem>
						<Label>Model</Label>
						<Value>{modelLabel}</Value>
					</InfoItem>
				)}
				{countingLabel && (
					<InfoItem>
						<Label>Counting</Label>
						<Value title={countingLabel}>{countingLabel}</Value>
					</InfoItem>
				)}
			</InfoRow>

			{/* Row 3: Message (full width) — hide when failed since error section covers it */}
			{job.message && !isFailed && (
				<InfoItem>
					<Label>Result</Label>
					<MessageText>{job.message}</MessageText>
				</InfoItem>
			)}

			{/* Row 4: Date run */}
			{job.created_at && (
				<InfoItem>
					<Label>Date run</Label>
					<Value>
						<DateFormatted inputDate={job.created_at} dateFormat="MMM d, yyyy h:mm a" />
					</Value>
				</InfoItem>
			)}

			{/* Error message — only when failed */}
			{isFailed && (
				<InfoItem>
					<Label>Error</Label>
					<ErrorText>Analysis failed.</ErrorText>
				</InfoItem>
			)}
		</>
	);
};

export default JobSection;
